const DISCIPLINE_ORDER = ['SN', 'PT', 'OT', 'ST', 'MSW', 'HHA'];
const DISCIPLINE_MATCH = [
  ['SN', /skilled nurs|\bsn\b|nursing|\brn\b|\blvn\b/i],
  ['PT', /physical|\bpt\b/i],
  ['OT', /occupational|\bot\b/i],
  ['ST', /speech|\bst\b|\bslp\b/i],
  ['MSW', /social|\bmsw\b/i],
  ['HHA', /aide|\bhha\b/i],
];

const disciplineRank = (name = '') => {
  const hit = DISCIPLINE_MATCH.find(([, re]) => re.test(name));
  return hit ? DISCIPLINE_ORDER.indexOf(hit[0]) : DISCIPLINE_ORDER.length;
};

const asList = (v) => {
  if (Array.isArray(v)) return v.map(x => (typeof x === 'object' && x ? Object.values(x).filter(Boolean).join(' - ') : String(x))).filter(Boolean);
  if (typeof v === 'string' && v.trim()) return v.split(/\n|;|•/).map(x => x.trim()).filter(Boolean);
  return [];
};

export const isDisciplineOrders = (key, value) =>
  Array.isArray(value) && value.length > 0 && value.every(v => v && typeof v === 'object' && 'discipline' in v);

export const normalizeDisciplineOrders = (value) => {
  const merged = {};
  value.forEach(({ discipline, frequency, orders }) => {
    const name = String(discipline || 'Other').trim();
    const slot = merged[name] || (merged[name] = { discipline: name, frequency: [], orders: [] });
    if (frequency) slot.frequency.push(String(frequency));
    slot.orders.push(...asList(orders));
  });
  return Object.values(merged)
    .sort((a, b) => disciplineRank(a.discipline) - disciplineRank(b.discipline))
    .map(d => ({ ...d, frequency: d.frequency.join('; ') }));
};

export const isStringList = (value) => Array.isArray(value) && value.length > 0 && value.every(v => typeof v === 'string');

export const DisciplineOrdersTable = ({ value, testId }) => (
  <table className="w-full text-sm border border-gray-200 rounded" data-testid={testId}>
    <thead className="bg-gray-100 text-gray-700">
      <tr><th className="text-left px-2 py-1 w-1/4">Discipline</th><th className="text-left px-2 py-1">Frequency &amp; Orders</th></tr>
    </thead>
    <tbody>
      {normalizeDisciplineOrders(value).map(d => (
        <tr key={d.discipline} className="border-t align-top">
          <td className="px-2 py-1.5 font-semibold text-gray-800">{d.discipline}</td>
          <td className="px-2 py-1.5">
            {d.frequency && <p className="font-medium text-gray-700 mb-1">{d.frequency}</p>}
            <ul className="list-none space-y-0.5">{d.orders.map((o, i) => <li key={i}>{o}</li>)}</ul>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

export const BulletList = ({ items, testId }) => (
  <ul className="list-none space-y-0.5" data-testid={testId}>
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ul>
);

// Draws a value into a jsPDF doc at (x, y); returns new y
// {bloodPressure: '132/84', HR: 78} -> 'Blood Pressure: 132/84, HR: 78'
const label = (k) => (/^[a-z]/.test(k) ? k.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, c => c.toUpperCase()) : k);
const readable = (v) => (Array.isArray(v) ? v.map(readable).join('; ')
  : v && typeof v === 'object' ? Object.entries(v).filter(([, x]) => x !== null && x !== undefined && x !== '').map(([k, x]) => `${label(k)}: ${readable(x)}`).join(', ')
  : String(v ?? ''));

export const drawPdfValue = (doc, key, value, x, y, maxWidth, ensureSpace) => {
  if (isDisciplineOrders(key, value)) {
    const colW = 32;
    normalizeDisciplineOrders(value).forEach(d => {
      const lines = [
        ...(d.frequency ? doc.splitTextToSize(d.frequency, maxWidth - colW - 4) : []),
        ...d.orders.flatMap(o => doc.splitTextToSize(o, maxWidth - colW - 4)),
      ];
      const h = Math.max(7, lines.length * 3.8 + 3);
      y = ensureSpace(y, h);
      doc.setDrawColor(200, 200, 200);
      doc.rect(x, y - 4, colW, h);
      doc.rect(x + colW, y - 4, maxWidth - colW, h);
      doc.setFont('helvetica', 'bold');
      doc.text(doc.splitTextToSize(d.discipline, colW - 3), x + 1.5, y);
      doc.setFont('helvetica', 'normal');
      doc.text(lines, x + colW + 2, y);
      y += h;
    });
    return y + 1;
  }
  if (isStringList(value)) {
    value.forEach(item => {
      const lines = doc.splitTextToSize(item, maxWidth);
      y = ensureSpace(y, lines.length * 5);
      doc.text(lines, x, y);
      y += lines.length * 5;
    });
    return y;
  }
  const empty = value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0) || /^\s*not (available|provided|specified|documented)\b/i.test(String(value));
  const text = (empty ? '' : typeof value === 'object' ? readable(value) : String(value)).replace(/^(\d{4})-(\d{2})-(\d{2})(T[\d:.]+Z?)?$/, '$2/$3/$1');
  const lines = doc.splitTextToSize(text, maxWidth);
  y = ensureSpace(y, lines.length * 5);
  doc.text(lines, x, y);
  return y + lines.length * 5;
};
